import qs from 'qs';
import { captureException, setContext, setUser, startSpan } from '@sentry/react';
import { RestApiError } from '../utils';
import { AuthUtils } from '@shared/utils';
import { HttpMethod, type ApiErrorData, type Headers, type IBaseRequestParams, type IRequestParams } from '../models';

const IGNORED_STATUS_ERRORS = [401];

export class Api {
  private static readonly baseUrl = process.env.API_URL!;

  protected static get<Response>(request: IRequestParams<null>): Promise<Response> {
    const url = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(
      () =>
        this._request<void, Response>({
          method: HttpMethod.Get,
          url,
          headers: request.headers,
          withAuthError: request.withAuthError,
          signal: request.signal,
          responseType: request.responseType,
        }),
      { withAuthError: request.withAuthError },
    );
  }

  protected static post<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    const url = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(
      () =>
        this._request<Request, Response>({
          method: HttpMethod.Post,
          url,
          data: request.data,
          headers: request.headers,
          withAuthError: request.withAuthError,
          signal: request.signal,
          responseType: request.responseType,
        }),
      { withAuthError: request.withAuthError },
    );
  }

  protected static put<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    const url = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(
      () =>
        this._request<Request, Response>({
          method: HttpMethod.Put,
          url,
          data: request.data,
          headers: request.headers,
          withAuthError: request.withAuthError,
          signal: request.signal,
          responseType: request.responseType,
        }),
      { withAuthError: request.withAuthError },
    );
  }

  protected static patch<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    const url = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(
      () =>
        this._request<Request, Response>({
          method: HttpMethod.Patch,
          url,
          data: request.data,
          headers: request.headers,
          withAuthError: request.withAuthError,
          signal: request.signal,
          responseType: request.responseType,
        }),
      { withAuthError: request.withAuthError },
    );
  }

  protected static delete<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    const url = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(
      () =>
        this._request<Request, Response>({
          method: HttpMethod.Delete,
          url,
          data: request.data,
          headers: request.headers,
          withAuthError: request.withAuthError,
          signal: request.signal,
          responseType: request.responseType,
        }),
      { withAuthError: request.withAuthError },
    );
  }

  private static _objectToQuery = (obj?: object) => {
    if (!obj) return '';
    return qs.stringify(obj, { addQueryPrefix: true, indices: false, allowDots: true, skipNulls: true });
  };

  private static async _wrapRequest<Response>(handler: () => Promise<Response>, options?: { withAuthError?: boolean }) {
    try {
      const result = await handler();
      return result;
    } catch (error) {
      if (RestApiError.isRestApiError(error)) {
        if (IGNORED_STATUS_ERRORS.includes(error.status) && !options?.withAuthError) {
          AuthUtils.logout();
          error.message = '';
          return Promise.reject(error);
        }
      }
      throw error;
    }
  }

  private static async _request<Request, Response>(request: IBaseRequestParams<Request>): Promise<Response> {
    return await startSpan({ op: 'http.client', name: `${request.method} ${this.baseUrl + request.url}` }, async (span) => {
      const traceId = span.spanContext().traceId;
      const spanId = span.spanContext().spanId;

      const parsedURL = new URL(this.baseUrl + request.url, location.origin);
      span.setAttribute('http.request.method', request.method);
      span.setAttribute('server.address', parsedURL.hostname);
      span.setAttribute('server.port', parsedURL.port || undefined);

      const headers =
        request.data && request.data instanceof FormData
          ? ({
              'Request-Id': `${traceId}-${spanId}`,
              ...(request.headers || {}),
            } as Headers)
          : ({
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'Request-Id': `${traceId}-${spanId}`,
              ...(request.headers || {}),
            } as Headers);
      if (!headers.Authorization && headers.Authorization !== '' && AuthUtils.authDataCached != null) {
        const accessToken = AuthUtils.authDataCached.accessToken;
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      }

      const options = {
        method: request.method,
        headers,
        body: request.data && request.data instanceof FormData ? request.data : request.data ? JSON.stringify(request.data) : null,
        signal: request.signal,
        targetAddressSpace: 'public',
      };

      const response = await fetch(this.baseUrl + request.url, options);

      span.setAttribute('http.response.status_code', response.status);
      span.setAttribute('http.response_content_length', Number(response.headers.get('content-length')));

      if (response.ok) {
        const contentType = response.headers.get('content-type');

        if (request.responseType === 'arrayBuffer')
          return {
            data: await response.arrayBuffer(),
            contentType: contentType,
            contentDisposition: response.headers.get('content-disposition'),
          };
        if (response.status === 204) return response.status as Response;
        if (contentType && contentType.indexOf('application/json') !== -1) return response.json();
        return {
          data: await response.arrayBuffer(),
          contentType: contentType,
          contentDisposition: response.headers.get('content-disposition'),
        } as Response;
      } else {
        if (request.signal?.aborted) return;
        let data: ApiErrorData = { message: 'Unknown error' };
        if (!IGNORED_STATUS_ERRORS.includes(response.status) || request.withAuthError) data = await response.json();
        else data.message = 'Unauthorized';

        const error = new RestApiError(data.message, response.status, data, request.method, request.url);
        setContext('Request', {
          requestId: `${traceId}-${spanId}`,
          method: request.method,
          url: request.url,
          status: response.status,
          message: data.message || 'Unknown error',
        });
        setUser({ id: AuthUtils.getUserId() });
        captureException(error);

        throw error;
      }
    });
  }
}
